import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Loader, Send } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import {
  getSessionInfo,
  sendADKMessageStreaming,
  ADK_APP_NAME,
  isAuthExpiredError,
  readSSEStream,
} from '../config/api';
import { PlatformType } from '../types';
import PlanSummaryCard, {
  parsePlanSummary,
  stripPlanSummary,
  hasUnreadablePlanSummary,
} from './PlanSummaryCard';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
}

interface PlanSummaryStepProps {
  sessionId: string;
  platform: PlatformType;
  onSummaryApproved: () => void;
  onLogout: () => void;
}

/** This step's own step number, as the backend reports it in `step_complete`. */
export const PLAN_SUMMARY_STEP = 4;

const OPEN_COMMAND = 'RUN_PLAN_SUMMARY';
const APPROVE_COMMAND = 'APPROVE_PLAN_SUMMARY';

/**
 * Step 4 — the last checkpoint before scenario planning.
 *
 * Replaces the Define Objectives step, which asked the user to watch an agent
 * run and then click Proceed with nothing to decide. Objectives now runs as a
 * background stage of RUN_PLAN_SUMMARY and its verdict is folded into one
 * reviewable summary of the whole plan.
 *
 * Approval is one click: the button sends APPROVE_PLAN_SUMMARY, the backend
 * answers with the step-4 marker, and the `step_complete` event advances the
 * flow. Nothing asks the user to click a second "proceed" afterwards.
 */
const PlanSummaryStep: React.FC<PlanSummaryStepProps> = ({
  sessionId,
  platform,
  onSummaryApproved,
  onLogout,
}) => {
  const [running, setRunning] = useState(false);
  const [approving, setApproving] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');

  const outputRef = useRef<HTMLDivElement>(null);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (!hasStarted.current && sessionId) {
      hasStarted.current = true;
      void sendMessage(`${OPEN_COMMAND} Session ID: ${sessionId}`, { echo: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const sendMessage = async (
    payload: string,
    { echo, echoText }: { echo: boolean; echoText?: string }
  ) => {
    if (!sessionId) return;

    const { userId } = getSessionInfo();
    setError('');

    if (echo && echoText) {
      setMessages(prev => [...prev, {
        id: `${Date.now()}-user`,
        type: 'user',
        content: echoText,
        timestamp: new Date(),
      }]);
    }

    setRunning(true);

    try {
      const stream = await sendADKMessageStreaming(
        ADK_APP_NAME, userId, sessionId, payload, platform
      );

      const assistantId = `${Date.now()}-assistant`;
      setMessages(prev => [...prev, {
        id: assistantId,
        type: 'assistant',
        content: '',
        timestamp: new Date(),
        isStreaming: true,
      }]);

      let fullContent = '';
      let approved = false;
      let expired = false;

      // readSSEStream buffers across reads. Parsing each read on its own
      // dropped the 2.4KB json:plan-summary frame whenever the network split
      // it, and the panel showed only the interstitial before it.
      await readSSEStream(stream, data => {
        if (data.type === 'content' && data.text) {
          fullContent += data.text;
          setMessages(prev => prev.map(msg =>
            msg.id === assistantId ? { ...msg, content: fullContent } : msg
          ));
        }
        // The backend derives this from one constant, so rewording the
        // agent's prose cannot silently stop the step advancing.
        if (data.type === 'step_complete' && data.step === PLAN_SUMMARY_STEP) {
          approved = true;
        }
        if (data.type === 'auth_expired') {
          expired = true;
        }
      });

      if (expired) {
        onLogout();
        return;
      }

      setMessages(prev => prev.map(msg =>
        msg.id === assistantId ? { ...msg, isStreaming: false } : msg
      ));
      setRunning(false);
      setApproving(false);

      if (approved) {
        onSummaryApproved();
      }
    } catch (caught) {
      setRunning(false);
      setApproving(false);
      // A Google access token lives an hour and nothing refreshes it, so a long
      // session ends here. Sending the user back to sign in beats leaving
      // "Unauthorized" on screen with no way forward.
      if (isAuthExpiredError(caught)) {
        onLogout();
        return;
      }
      console.error('Plan summary request failed:', caught);
      setError(caught instanceof Error ? caught.message : 'Something went wrong.');
    }
  };

  const handleSendInput = () => {
    const text = inputValue.trim();
    if (!text || running) return;
    setInputValue('');
    void sendMessage(
      `${OPEN_COMMAND}\nSession ID: ${sessionId}\nUser Message: ${text}`,
      { echo: true, echoText: text }
    );
  };

  // Text sitting unsent in the box. Approving would throw it away.
  const hasUnsentEdit = inputValue.trim().length > 0;

  const handleApprove = () => {
    if (running || hasUnsentEdit) return;
    setApproving(true);
    void sendMessage(`${APPROVE_COMMAND} Session ID: ${sessionId}`, { echo: false });
  };

  return (
    <div className="config-content">
      <motion.div
        className="config-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="config-title">Step 4: Plan Summary</h2>
        <p className="config-subtitle">
          Review the plan before scenario planning. Type a change to alter it.
        </p>

        <div className="agent-output-container" style={{ marginTop: '24px' }}>
          <div className="agent-output-header">
            <MessageSquare size={18} />
            <span>Plan Summary</span>
            {running && <Loader className="spinner" size={16} />}
          </div>

          <div className="agent-output-content" ref={outputRef} style={{ flex: 1 }}>
            <AnimatePresence>
              {messages.map(message => (
                <motion.div
                  key={message.id}
                  className={`message ${message.type}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="message-content">
                    {(() => {
                      // The summary arrives as a json:plan-summary block, which
                      // the streaming guard preserves. Lay it out; render any
                      // prose around it as markdown. Until the block has fully
                      // streamed there is nothing to parse, so the raw text is
                      // hidden rather than shown half-formed.
                      const payload = parsePlanSummary(message.content);
                      const prose = payload
                        ? stripPlanSummary(message.content)
                        : message.content.replace(/```json:plan-summary[\s\S]*$/, '');
                      return (
                        <>
                          {prose.trim() && (
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm, remarkBreaks]}
                              className="markdown-content"
                            >
                              {prose}
                            </ReactMarkdown>
                          )}
                          {payload && <PlanSummaryCard payload={payload} />}
                          {!payload && hasUnreadablePlanSummary(message.content) && (
                            <div style={{ fontSize: '13px', color: '#b45309' }}>
                              The summary arrived in a form this panel could not read.
                              Reload the step to try again.
                            </div>
                          )}
                        </>
                      );
                    })()}
                    {message.isStreaming && <span className="cursor">▊</span>}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {error && (
            <div style={{ padding: '8px 16px', color: '#b91c1c', fontSize: '13px' }}>
              {error}
            </div>
          )}

          {/* A real form, so Enter submits the edit. It used to be a bare
              input with onKeyPress, and the only prominent button on the panel
              was "Approve & Proceed" — so a typed change was approved away
              instead of applied, and the plan moved on unchanged. */}
          <form
            className="chat-input-container"
            onSubmit={e => { e.preventDefault(); handleSendInput(); }}
          >
            <input
              type="text"
              className="chat-input"
              placeholder='Change something — e.g. "set budget to 60000"'
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendInput();
                }
              }}
              disabled={running}
            />
            <button
              type="submit"
              className="send-button"
              disabled={running || !inputValue.trim()}
              title="Apply this change"
            >
              <Send size={18} />
            </button>
          </form>

          <div style={{ padding: '12px 16px 16px', flexShrink: 0, borderTop: '1px solid #e5e7eb' }}>
            <button
              type="button"
              className="continue-button"
              onClick={handleApprove}
              disabled={running || hasUnsentEdit}
              style={{ width: '100%', opacity: hasUnsentEdit ? 0.5 : undefined }}
            >
              {approving ? 'Approving…' : 'Approve & Proceed to Scenarios'}
            </button>
            {/* Approving with text still in the box would silently discard the
                change and advance the plan unchanged. */}
            {hasUnsentEdit && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#b45309', textAlign: 'center' }}>
                Send your change first, or clear the box to approve as it is.
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PlanSummaryStep;
